import { useCallback, useState, type Dispatch, type SetStateAction } from 'react';

/**
 * State that belongs to `key` (a query, a run, a selected item). When the key changes, the value starts
 * over as `initial` in the same render, instead of an effect resetting it a frame later; returning to an
 * earlier key starts over too. A value set later through a setter made for an earlier key (say, after an
 * await) is dropped rather than shown against the new key. The setter takes a value or an updater.
 *
 * Use a `key` on the component instead when everything inside it should start over.
 */
export function useKeyedState<T>(key: unknown, initial: T): [T, Dispatch<SetStateAction<T>>] {
    const [state, setState] = useState<{ key: unknown; value: T }>({ key, value: initial });
    const current = Object.is(state.key, key);
    // Adjusting state while rendering: React re-renders this component at once, before the screen updates.
    if (!current) setState({ key, value: initial });
    const set = useCallback<Dispatch<SetStateAction<T>>>((next) => setState(previous => {
        const base = Object.is(previous.key, key) ? previous.value : initial;
        return { key, value: typeof next === 'function' ? (next as (value: T) => T)(base) : next };
        // initial is the value for a fresh key; a setter is made per key.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [key]);
    return [current ? state.value : initial, set];
}
